import { publicApi, privateApi } from '@/config/axios.config';
import { sessionService } from '@/shared/services/session.service';
import { LoginCredentials, User, RegisterData } from '@/shared/types';

// Interfaces específicas para la API
export interface LoginResponse {
  user: User;
  token?: string;
  refreshToken?: string;
  role: string;
  tenantId: string;
  tenant: string;
  redirectUrl?: string;
  expiresAt: string | Date;
}

export interface RegisterResponse {
  user: User;
  token?: string;
  refreshToken?: string;
  role: string;
  tenantId: string;
  tenant: string;
  redirectUrl?: string;
  expiresAt: string | Date;
}

export interface RefreshTokenResponse {
  token?: string;
  refreshToken?: string;
  role: string;
  tenantId: string;
  tenant: string;
  redirectUrl?: string;
  expiresAt: string | Date;
  user: User;
}

export interface OnboardingData {
  firstName: string;
  lastName: string;
  phone?: string;
  company?: string;
  /** GH#2107 — ver `DatosFiscalesOnboardingDto` (#2117). */
  fiscal?: import('@/features/onboarding/utils/fiscal-alta').FiscalOnboarding;
}

export interface OnboardingResponse {
  message: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    fullName: string;
    onboardingCompleted: boolean;
  };
}

// GH#1826 — aceptación de invitación de agente (público, sin JWT: el token es la auth).
export interface InvitationInfo {
  email: string;
  firstName?: string;
  lastName?: string;
  tenantName: string;
  expiresAt: string;
}

export interface AcceptInvitationPayload {
  token: string;
  password: string;
  acceptTerms: boolean;
  acceptPrivacy: boolean;
  firstName?: string;
  lastName?: string;
}

const buildTenantHeaders = (overrideTenantId?: string) => {
  const currentUser = sessionService.getUser();
  const tenantId = overrideTenantId ?? currentUser?.tenantId ?? null;
  return tenantId ? { 'x-tenant-id': tenantId } : {};
};

// API de autenticación
export const authApi = {
  // Login
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    const response = await publicApi.post('/auth/login', credentials);
    return response.data.data; // Extraer datos de la estructura { success: true, data: {...} }
  },

  // Registro
  register: async (
    userData: RegisterData,
    tenantId?: string
  ): Promise<RegisterResponse> => {
    const response = await publicApi.post('/auth/register', userData, {
      headers: buildTenantHeaders(tenantId ?? userData.tenant),
    });
    return response.data.data; // Extraer datos de la estructura { success: true, data: {...} }
  },

  // Logout
  logout: async (): Promise<void> => {
    // Send refresh token in body for cross-domain compatibility
    const refreshToken = sessionService.getRefreshToken();
    await privateApi.post(
      '/auth/logout',
      { refreshToken },
      { headers: buildTenantHeaders() }
    );
  },

  // Refresh token
  refreshToken: async (
    refreshTokenOverride?: string
  ): Promise<RefreshTokenResponse> => {
    // Send refresh token in body for cross-domain compatibility (cookies don't cross domains)
    const storedRefresh = refreshTokenOverride || sessionService.getRefreshToken();
    const response = await publicApi.post(
      '/auth/refresh',
      { refreshToken: storedRefresh },
      { headers: buildTenantHeaders() }
    );
    return response.data.data; // Extraer datos de la estructura { success: true, data: {...} }
  },

  // Obtener perfil del usuario actual
  getProfile: async (): Promise<User> => {
    const response = await privateApi.get('/auth/me', {
      headers: buildTenantHeaders(),
    });
    return response.data.data; // Extraer datos de la estructura { success: true, data: {...} }
  },

  // Obtener perfil del usuario con redirectUrl (para OAuth)
  getProfileWithRedirect: async (): Promise<{ user: User; redirectUrl: string }> => {
    const response = await privateApi.get('/auth/me', {
      headers: buildTenantHeaders(),
    });
    // El interceptor del backend envuelve en { success: true, data: { user: {...}, redirectUrl: "..." } }
    return response.data.data;
  },

  // Verificar token
  verifyToken: async (): Promise<{ valid: boolean; user?: User }> => {
    try {
      const user = await authApi.getProfile();
      return { valid: true, user };
    } catch (error) {
      return { valid: false };
    }
  },

  // Cambiar contraseña (pendiente de implementación en backend)
  changePassword: async (
    currentPassword: string,
    newPassword: string
  ): Promise<void> => {
    await privateApi.post(
      '/auth/change-password',
      {
        currentPassword,
        newPassword,
      },
      { headers: buildTenantHeaders() }
    );
  },

  // Solicitar reset de contraseña
  requestPasswordReset: async (email: string): Promise<void> => {
    await publicApi.post('/auth/forgot-password', { email });
  },

  // Reset de contraseña con código OTP
  resetPassword: async (email: string, code: string, newPassword: string): Promise<void> => {
    await publicApi.post('/auth/reset-password', {
      email,
      code,
      newPassword,
    });
  },

  // Completar onboarding
  completeOnboarding: async (
    onboardingData: OnboardingData
  ): Promise<OnboardingResponse> => {
    const response = await privateApi.post(
      '/auth/complete-onboarding',
      onboardingData,
      {
        headers: buildTenantHeaders(),
      }
    );
    return response.data.data; // Extraer datos de la estructura { success: true, data: {...} }
  },

  // Auto-completar onboarding
  autoCompleteOnboarding: async (): Promise<{
    user: User;
    wasAutoCompleted: boolean;
  }> => {
    const response = await privateApi.post(
      '/auth/auto-complete-onboarding',
      {},
      {
        headers: buildTenantHeaders(),
      }
    );
    return response.data; // Respuesta directa del endpoint
  },

  // GH#1826 — datos para la página de aceptación (público, 5/min/IP). El backend
  // devuelve 400 INVITACION_INVALIDA (mismo error para vencido/usado/inexistente).
  invitationInfo: async (token: string): Promise<InvitationInfo> => {
    const response = await publicApi.post('/auth/invitation-info', { token });
    return response.data?.data ?? response.data; // envelope { success, data }
  },

  // GH#1826 — crea la cuenta del agente. Público (el token es la auth), exento de CSRF.
  // 400 INVITACION_INVALIDA = token vencido/usado/inexistente; 400 CONSENTIMIENTO_REQUERIDO
  // si falta una casilla (el token NO se gasta).
  acceptInvitation: async (payload: AcceptInvitationPayload): Promise<void> => {
    await publicApi.post('/auth/accept-invitation', payload);
  },
};
